import { ShippingEmissionTimeseries } from '@lune-fe/dapi-models'
import {
    EmissionEstimationIcons,
    emissionsChartColors,
    emissionsChartLabels,
    logisticsChartOrdering,
} from '@lune-fe/lune-components-lib'

import EmissionsBreakdownTable, { EmissionsBreakdownItem } from 'components/EmissionsBreakdownTable'
import WhiteBoxContainerWithContent from 'components/WhiteBoxContainerWithContent'

const EmissionsByTransportMode = ({
    emissionTimeseries,
}: {
    emissionTimeseries?: ShippingEmissionTimeseries
}) => {
    if (!emissionTimeseries || emissionTimeseries.length === 0) {
        return null
    }

    const totals = emissionTimeseries.reduce(
        (acc, item) => ({
            sea: acc.sea + item.sea,
            air: acc.air + item.air,
            road: acc.road + item.road,
            rail: acc.rail + item.rail,
            inlandWaterway: acc.inlandWaterway + item.inlandWaterway,
            logisticSite: acc.logisticSite + item.logisticSite,
        }),
        { sea: 0, air: 0, road: 0, rail: 0, inlandWaterway: 0, logisticSite: 0 },
    )

    const totalEmissions = Object.values(totals).reduce((sum, value) => sum + value, 0)

    const transportModeData: EmissionsBreakdownItem[] = [
        {
            label: emissionsChartLabels.sea,
            value: totals.sea,
            percentage: totalEmissions > 0 ? (totals.sea / totalEmissions) * 100 : 0,
            color: emissionsChartColors.sea,
            icon: EmissionEstimationIcons.ship,
            order: logisticsChartOrdering.sea,
        },
        {
            label: emissionsChartLabels.road,
            value: totals.road,
            percentage: totalEmissions > 0 ? (totals.road / totalEmissions) * 100 : 0,
            color: emissionsChartColors.road,
            icon: EmissionEstimationIcons.truck,
            order: logisticsChartOrdering.road,
        },
        {
            label: emissionsChartLabels.air,
            value: totals.air,
            percentage: totalEmissions > 0 ? (totals.air / totalEmissions) * 100 : 0,
            color: emissionsChartColors.air,
            icon: EmissionEstimationIcons.plane,
            order: logisticsChartOrdering.air,
        },
        {
            label: emissionsChartLabels.rail,
            value: totals.rail,
            percentage: totalEmissions > 0 ? (totals.rail / totalEmissions) * 100 : 0,
            color: emissionsChartColors.rail,
            icon: EmissionEstimationIcons.train,
            order: logisticsChartOrdering.rail,
        },
        {
            label: emissionsChartLabels.inlandWaterway,
            value: totals.inlandWaterway,
            percentage: totalEmissions > 0 ? (totals.inlandWaterway / totalEmissions) * 100 : 0,
            color: emissionsChartColors.inlandWaterway,
            icon: EmissionEstimationIcons.waterway,
            order: logisticsChartOrdering.inlandWaterway,
        },
        {
            label: emissionsChartLabels.logisticSite,
            value: totals.logisticSite,
            percentage: totalEmissions > 0 ? (totals.logisticSite / totalEmissions) * 100 : 0,
            color: emissionsChartColors.logisticSite,
            icon: EmissionEstimationIcons.warehouse,
            order: logisticsChartOrdering.logisticSite,
        },
    ]
        .filter((item) => item.value > 0)
        .sort((a, b) => a.order - b.order)

    return (
        <WhiteBoxContainerWithContent
            title="Emissions by transport mode"
            sx={{
                width: '100%',
                flex: '1 1 auto',
                '> .MuiBox-root:first-of-type': {
                    p: 3,
                },
                '> .MuiBox-root:last-of-type': {
                    p: 0,
                },
            }}
        >
            <EmissionsBreakdownTable
                items={transportModeData}
                headerLeftLabel="Mode of transport"
                headerRightLabel="tCO₂e"
            />
        </WhiteBoxContainerWithContent>
    )
}

export default EmissionsByTransportMode
